import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Newspaper,
  GraduationCap,
  Video,
  HelpCircle,
  ExternalLink,
  ChevronDown,
  ArrowUpRight,
  Play,
} from "lucide-react";
import PageHero from "../../components/shared/PageHero";
import CtaBand from "../../components/shared/CtaBand";
import { VIDEOS } from "../../data/content";
import "./LearningCentrePage.css";

const GUIDES = [
  {
    title: "SIP vs Lumpsum",
    desc: "When to stagger investments and when a lumpsum may suit surplus capital.",
  },
  {
    title: "Asset Allocation Basics",
    desc: "How equity, debt, and gold work together across different life stages.",
  },
  {
    title: "Reading Fund Factsheets",
    desc: "Expense ratios, AUM, portfolio holdings, and risk measures demystified.",
  },
  {
    title: "IPO Application Checklist",
    desc: "ASBA, UPI mandates, and what to verify in the RHP before you bid.",
  },
];

const UPDATES = [
  {
    title: "RBI policy stance — what investors should watch",
    date: "Aug 2026",
    tag: "Macro",
  },
  {
    title: "Mutual fund category flows this quarter",
    date: "Jul 2026",
    tag: "Funds",
  },
  {
    title: "Gold & SGB — allocation thoughts for Indian portfolios",
    date: "Jul 2026",
    tag: "Commodities",
  },
];

const NEWS = [
  {
    title: "Sensex & Nifty: key levels for long-term SIPs",
    source: "Market Desk",
    summary: "Illustrative commentary on staying invested through volatility.",
  },
  {
    title: "Debt markets: yields and fixed-income opportunity set",
    source: "Fixed Income",
    summary: "How changing yields may affect bond and debt fund choices.",
  },
  {
    title: "SME funding: schemes founders should know",
    source: "Business Finance",
    summary: "A static sample card highlighting government-linked support themes.",
  },
];

const FAQS = [
  {
    q: "Who is the Learning Centre for?",
    a: "Individuals, first-time investors, students, and business owners who want practical Indian finance education — not jargon.",
  },
  {
    q: "Is Poonji Vidhya part of Poonji Mitra?",
    a: "Poonji Vidhya is our financial education platform for markets, investing, and career pathways. Visit poonjividhya.com to explore programmes.",
  },
  {
    q: "Are blogs investment advice?",
    a: "Blogs and guides are educational. Investment decisions should consider your goals, risk profile, and a personalised consultation.",
  },
  {
    q: "How do I get started?",
    a: "Read a guide, watch a video, or book a free consultation — we'll meet you at your current knowledge level.",
  },
];

export default function LearningCentrePage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="learn-page">
      <PageHero
        badge="Learning Centre"
        title="Learn markets. Build confidence."
        subtitle="Blogs, guides, market updates, videos, and FAQs — plus Poonji Vidhya for structured financial education."
        actions={[
          { label: "Explore Blogs", to: "/blogs", primary: true },
          {
            label: "Visit Poonji Vidhya",
            to: "https://www.poonjividhya.com/",
            external: true,
          },
        ]}
      />

      <section className="pm-section learn-hub">
        <div className="pm-container">
          <div className="learn-hub__grid">
            <Link to="/blogs" className="pm-card learn-hub__card">
              <BookOpen size={24} />
              <h3>Blogs</h3>
              <p>Insights on investing, debt, retirement, and portfolio management.</p>
              <span>
                Read blogs <ArrowUpRight size={15} />
              </span>
            </Link>
            <a
              href="https://www.poonjividhya.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="pm-card learn-hub__card"
            >
              <GraduationCap size={24} />
              <h3>Poonji Vidhya</h3>
              <p>Structured learning for markets, investing, and finance careers.</p>
              <span>
                Visit site <ExternalLink size={15} />
              </span>
            </a>
            <a href="#videos" className="pm-card learn-hub__card">
              <Video size={24} />
              <h3>Videos</h3>
              <p>Short explainers on SIPs, diversification, and wealth journeys.</p>
              <span>
                Watch below <ArrowUpRight size={15} />
              </span>
            </a>
          </div>
        </div>
      </section>

      <section className="pm-section learn-updates">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Market Updates
          </span>
          <h2 className="pm-section-title">Stay informed</h2>
          <div className="learn-updates__grid">
            {UPDATES.map((u) => (
              <article key={u.title} className="pm-card learn-update-card">
                <span className="learn-tag">{u.tag}</span>
                <h3>{u.title}</h3>
                <time>{u.date}</time>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-section learn-guides">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Investment Guides
          </span>
          <h2 className="pm-section-title">Practical primers</h2>
          <div className="learn-guides__grid">
            {GUIDES.map((g) => (
              <article key={g.title} className="pm-card learn-guide-card">
                <Newspaper size={20} className="learn-icon" />
                <h3>{g.title}</h3>
                <p>{g.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-section learn-videos" id="videos">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Videos
          </span>
          <h2 className="pm-section-title">Watch & learn</h2>
          <div className="learn-videos__grid">
            {VIDEOS.map((v) => (
              <a
                key={v.id}
                href={`https://www.youtube.com/watch?v=${v.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pm-card learn-video-card"
              >
                <div className="learn-video-card__thumb">
                  <img src={v.thumbnail} alt="" />
                  <span className="learn-video-card__play">
                    <Play size={22} fill="currentColor" />
                  </span>
                </div>
                <h3>{v.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-section learn-faq">
        <div className="pm-container learn-faq__wrap">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            <HelpCircle size={12} />
            FAQs
          </span>
          <h2 className="pm-section-title">Common questions</h2>
          <div className="learn-faq__list">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className={`pm-card learn-faq__item ${open ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="learn-faq__q"
                    onClick={() => setOpenFaq(open ? null : i)}
                    aria-expanded={open}
                  >
                    {f.q}
                    <ChevronDown size={18} />
                  </button>
                  {open && <p className="learn-faq__a">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pm-section learn-news">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Financial News
          </span>
          <h2 className="pm-section-title">Sample desk notes</h2>
          <p className="pm-section-sub">
            Static sample cards for layout — replace with live feed when ready.
          </p>
          <div className="learn-news__grid">
            {NEWS.map((n) => (
              <article key={n.title} className="pm-card learn-news-card">
                <span className="learn-tag">{n.source}</span>
                <h3>{n.title}</h3>
                <p>{n.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to put learning into action?"
        subtitle="Book a free consultation or explore Poonji Vidhya for deeper programmes."
        primary={{ label: "Book Free Consultation", to: "/contact" }}
        secondary={{ label: "Review My Portfolio", to: "/portfolio-review" }}
      />
    </div>
  );
}
